// ---------------------------------------------------------------------------
// PB-017 Phase 2 — Custom Domain Connection CQRS Event Catalog (vocabulary)
// ---------------------------------------------------------------------------
// Story PB-017: "Custom domain connection supports verification/SSL and one
// domain serving pages + adjacent services."
//
// Decision packet at `docs/vision_execution/openclaw/PB-017-DECISION-PACKET.md`
// sequences the 4-DOM-packet gate (DOM-018 verification state machine →
// DOM-010 CF Custom Hostnames + DOM-011 coexistence → DOM-007 multi-record
// idempotent apply → PB-017 6-case acceptance test).
//
// This Phase 2 file locks the CQRS event types the PB-017 closure tranche
// will emit so the future page-serve resolver, audit-runner, and DLQ
// consumers read one source of truth instead of hand-rolling
// `page_builder.custom_domain_connected@v1` and siblings.
//
// Per Governance Rule #7 ("Event Catalog"): every event type catalogued
// BEFORE Phase 3 route implementation, so the writer emits structured
// events matching the locked connection-stage / failure-reason vocabularies.
//
// What this file is NOT:
//  - It does not emit events — that's the future PB-017 closure tranche.
//  - It does not change DOM-018 verification schema (additive only).
//  - It does not implement CF Custom Hostnames calls (DOM-010 territory).
//
// What it IS:
//  - The single source of truth for the 3 PB-017 connection event strings.
//  - Typed payload contracts so the emitter + DLQ consumer cannot drift.
//  - Runtime narrowing guards so DLQ/audit-runner reject unknown event types
//    or unknown failure reasons.
// ---------------------------------------------------------------------------

import { DOM_018_VERIFICATION_FAILURE_REASON_VOCAB } from "./dom-018-domain-verification-event-catalog";

/**
 * Closed catalog of the 3 PB-017 CQRS event types. Versioned with the `@v1`
 * suffix per platform convention (Governance Rule #7).
 *
 * One business fact per event (Governance Rule #6):
 *  - `custom_domain_connection_initiated` — operator submitted the connection
 *    wizard for a tenant custom domain. Captures the chosen connection mode
 *    so the audit MV can split adoption between the supported strategies.
 *  - `custom_domain_connection_completed` — page-serve resolver + SSL
 *    issuance both reported healthy; the tenant domain now serves pages and
 *    adjacent services on the unified row (Doc 87).
 *  - `custom_domain_connection_failed` — connection attempt terminated with
 *    a closed-set failure reason. Carries the failure reason for the UI
 *    badge + audit trail.
 *
 * Sized as a tuple so future PB-017 lifecycle events
 * (`custom_domain_disconnected@v1`, `custom_domain_ssl_rotated@v1`) can
 * extend without rename.
 */
export const PB_017_CUSTOM_DOMAIN_CONNECTION_EVENT_TYPES = [
  "page_builder.custom_domain_connection_initiated@v1",
  "page_builder.custom_domain_connection_completed@v1",
  "page_builder.custom_domain_connection_failed@v1",
] as const;

export type Pb017CustomDomainConnectionEventType =
  (typeof PB_017_CUSTOM_DOMAIN_CONNECTION_EVENT_TYPES)[number];

/**
 * Closed catalog of connection modes the wizard supports. Mirrors the DOM-017
 * three-strategy contract (NS-transfer in_progress, coexistence + buy-new
 * BLOCKED) plus the apex/subdomain split that DOM-018 verification uses.
 */
export const PB_017_CONNECTION_MODE_VOCAB = [
  "nameserver_transfer",
  "coexistence_cname",
  "operator_assisted",
] as const;

export type Pb017ConnectionModeVocab =
  (typeof PB_017_CONNECTION_MODE_VOCAB)[number];

/**
 * Closed catalog of failure reasons. Strictly superset of DOM-018 verification
 * reasons (the connection flow inherits them VERBATIM — same codes, same case)
 * plus PB-017-specific issuance reasons (SSL provisioning, page-serve resolver
 * wiring). Each failed connection MUST be attributable to one of these reasons
 * so the audit MV can quantify how often each guard fires.
 *
 * The DOM-018 codes are spread in directly from `DOM_018_VERIFICATION_FAILURE_REASON_VOCAB`
 * — renaming them would break the "single source of truth" contract for
 * downstream consumers when the PB-017 connection flow delegates to the
 * DOM-018 verifier and the verifier's `failureReason` would otherwise need a
 * lossy remap. Codex P1 on PR #1974 — keep them lock-step.
 *
 * `RATE_LIMITED` and `PROVIDER_ERROR` are already present in DOM-018, so they
 * arrive via the spread and are NOT duplicated here.
 */
export const PB_017_CONNECTION_FAILURE_REASON_VOCAB = [
  // DOM-018 verification family (carried through the connection flow verbatim):
  ...DOM_018_VERIFICATION_FAILURE_REASON_VOCAB,
  // SSL issuance family (DOM-010 CF Custom Hostnames):
  "SSL_ISSUANCE_FAILED",
  "SSL_VALIDATION_TIMEOUT",
  // Page-serve resolver wiring:
  "RESOLVER_BINDING_FAILED",
  // Operator/policy (DOM-018 already carries `RATE_LIMITED` + `PROVIDER_ERROR`):
  "OPERATOR_ABORTED",
] as const;

export type Pb017ConnectionFailureReasonVocab =
  (typeof PB_017_CONNECTION_FAILURE_REASON_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                    */
/* -------------------------------------------------------------------------- */

export function isPb017CustomDomainConnectionEventType(
  value: unknown,
): value is Pb017CustomDomainConnectionEventType {
  return (
    typeof value === "string" &&
    (
      PB_017_CUSTOM_DOMAIN_CONNECTION_EVENT_TYPES as readonly string[]
    ).includes(value)
  );
}

export function isPb017ConnectionModeVocab(
  value: unknown,
): value is Pb017ConnectionModeVocab {
  return (
    typeof value === "string" &&
    (PB_017_CONNECTION_MODE_VOCAB as readonly string[]).includes(value)
  );
}

export function isPb017ConnectionFailureReasonVocab(
  value: unknown,
): value is Pb017ConnectionFailureReasonVocab {
  return (
    typeof value === "string" &&
    (PB_017_CONNECTION_FAILURE_REASON_VOCAB as readonly string[]).includes(
      value,
    )
  );
}

/* -------------------------------------------------------------------------- */
/* Per-event payload contracts                                                 */
/* -------------------------------------------------------------------------- */

export interface Pb017ConnectionInitiatedPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** FK into the Doc 87 unified `custom_domains` row. */
  customDomainId: string;
  /** Apex/subdomain the operator submitted (already normalized lowercase). */
  domain: string;
  /** Wizard-selected connection strategy (closed vocab). */
  mode: Pb017ConnectionModeVocab;
  /** Operator who initiated the connection (audit trail). */
  initiatedBy: string;
  /** ISO-8601 — when the wizard submission was accepted. */
  initiatedAt: string;
}

export interface Pb017ConnectionCompletedPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** FK into the Doc 87 unified `custom_domains` row. */
  customDomainId: string;
  /** Apex/subdomain that now serves pages + adjacent services. */
  domain: string;
  /** Wizard-selected connection strategy (closed vocab). */
  mode: Pb017ConnectionModeVocab;
  /** Total connection attempts before success (DOM-018 attempts counter). */
  attempts: number;
  /** ISO-8601 — when the page-serve resolver + SSL both reported healthy. */
  completedAt: string;
}

export interface Pb017ConnectionFailedPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** FK into the Doc 87 unified `custom_domains` row. */
  customDomainId: string;
  /** Apex/subdomain that failed to connect. */
  domain: string;
  /** Wizard-selected connection strategy (closed vocab). */
  mode: Pb017ConnectionModeVocab;
  /** Closed-set failure reason — drives the UI badge + audit MV. */
  failureReason: Pb017ConnectionFailureReasonVocab;
  /** Connection attempts before terminal failure. */
  attempts: number;
  /** ISO-8601 — when the writer logged terminal failure. */
  failedAt: string;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (page-serve resolver, audit-runner, DLQ) `switch (event.type)` and pull
 * the right payload without any-casting.
 */
export type Pb017CustomDomainConnectionEvent =
  | {
      type: "page_builder.custom_domain_connection_initiated@v1";
      payload: Pb017ConnectionInitiatedPayload;
    }
  | {
      type: "page_builder.custom_domain_connection_completed@v1";
      payload: Pb017ConnectionCompletedPayload;
    }
  | {
      type: "page_builder.custom_domain_connection_failed@v1";
      payload: Pb017ConnectionFailedPayload;
    };
